'use client';

import { useCallback, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { useDebouncedValue } from './useDebouncedValue';

export interface EventSearchResult {
    eventTicker: string;
    eventTitle: string;
    seriesTicker: string;
}

const MIN_QUERY_LENGTH = 2;

async function fetchEventSearch(query: string): Promise<EventSearchResult[]> {
    const res = await fetch(`/api/widgets/search/series?query=${encodeURIComponent(query)}&page_size=8`);
    if (!res.ok) throw new Error(`Event search failed: ${res.status}`);
    const data = (await res.json()) as {
        current_page?: { event_ticker: string; event_title: string; series_ticker: string }[];
    };
    return (data.current_page ?? []).map((s) => ({
        eventTicker: s.event_ticker,
        eventTitle: s.event_title,
        seriesTicker: s.series_ticker,
    }));
}

/**
 * Single source for the event-search behavior previously duplicated across
 * SymbolDetail / Watchlist / AdvancedChart: debounce + autocomplete +
 * arrow-key navigation + Enter-to-select. Hits the `/api/widgets/search/series`
 * route (keeps the main-app api module out of the client bundle).
 */
export function useEventSearch(opts: { onSelect?: (result: EventSearchResult) => void } = {}) {
    const [query, setQuery] = useState('');
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const debounced = useDebouncedValue(query, 300);
    const enabled = debounced.trim().length >= MIN_QUERY_LENGTH;

    const { data, isFetching, isError } = useQuery({
        queryKey: ['widget-event-search', debounced],
        queryFn: () => fetchEventSearch(debounced),
        enabled,
        staleTime: 60_000,
    });

    const results = enabled ? (data ?? []) : [];

    const reset = useCallback(() => {
        setQuery('');
        setHighlightedIndex(-1);
    }, []);

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            if (results.length === 0) return;
            if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlightedIndex((i) => (i + 1) % results.length);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlightedIndex((i) => (i <= 0 ? results.length - 1 : i - 1));
            } else if (e.key === 'Enter') {
                const choice = results[highlightedIndex] ?? results[0];
                if (choice) {
                    e.preventDefault();
                    opts.onSelect?.(choice);
                    reset();
                }
            }
        },
        [results, highlightedIndex, opts, reset],
    );

    return {
        query,
        setQuery,
        results,
        isLoading: enabled && isFetching,
        isError,
        highlightedIndex,
        setHighlightedIndex,
        onKeyDown,
        reset,
    };
}
